/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { injectable } from 'inversify'
import { IRequestHistoryApiClient } from './contracts'
import { ICoverageDiscoveryRequestHistoryGridRow, ICoverageDiscoveryRequestHistoryGridCriteria, ICoverageDiscoveryRequestHistoryGridResults } from '../RequestHistoryStateManager/contracts'
import { TransactionStatus } from '../../configuration/Enums/TransactionStaus'

@injectable()
export class MockRequestHistoryApiClient implements IRequestHistoryApiClient {
	public items: ICoverageDiscoveryRequestHistoryGridRow[] = []

	private firstNames = ['John', 'Betty', 'Alice', 'Peter']
	private lastNames = ['Smith', 'Doe', 'Roberts']

	constructor() {
		for (let x = 1; x <= 100; x++) {
			this.items.push({
				id: x,
				isSelected: false,
				coverageDiscoveryResponseId: x,
				batchRunId: x,
				requestData: {
					firstName: this.firstNames[Math.ceil((Math.random() * this.firstNames.length) % this.firstNames.length) - 1],
					lastName: this.lastNames[Math.ceil((Math.random() * this.lastNames.length) % this.lastNames.length) - 1],
					dob: new Date(1970, 1, Math.round((Math.random() * 10000) % 10000)),
					ssn: `${this.generateRandomNumberString(3)}-${this.generateRandomNumberString(2)}-${this.generateRandomNumberString(4)}`,
					payerNames: [`Payer1`, `payer2`, `payer3`],
					npi: `npi${x}`,
					facility: `facility${x}`,
				},
				transactionStatusId: TransactionStatus.Complete,
				reviewedByUser: `User${x}`,
				reviewedDate: new Date(2020, 1, 1, Math.round((Math.random() * 500) % 500), Math.round((Math.random() * 1000) % 1000), 0, 0),
				reviewed: false,
				requestor: `${this.lastNames[Math.ceil((Math.random() * this.lastNames.length) % this.lastNames.length) - 1]}, 
					${this.firstNames[Math.ceil((Math.random() * this.firstNames.length) % this.firstNames.length) - 1]}`,
				requestDate: new Date(2020, 1, 1, Math.round((Math.random() * 500) % 500), Math.round((Math.random() * 1000) % 1000), 0, 0),
			})
		}
	}

	list(batchRunId: number, criteria: ICoverageDiscoveryRequestHistoryGridCriteria): Promise<ICoverageDiscoveryRequestHistoryGridResults> {
		const result: ICoverageDiscoveryRequestHistoryGridResults = {
			items: this.items.slice(),
			total: 0,
		}

		if (criteria.searchTerm) {
			if (criteria.searchTerm.firstName) {
				result.items = result.items.filter(x => x.requestData.firstName.toLowerCase().includes(criteria.searchTerm.firstName.toLowerCase()))
			}
			if (criteria.searchTerm.lastName) {
				result.items = result.items.filter(x => x.requestData.lastName.toLowerCase().includes(criteria.searchTerm.lastName.toLowerCase()))
			}
			if (criteria.searchTerm.ssn) {
				result.items = result.items.filter(x =>
					x.requestData.ssn
						.replace(/-/g, '')
						.trim()
						.includes(criteria.searchTerm.ssn.replace(/-/g, '').trim()),
				)
			}
			if (criteria.searchTerm.dob) {
				const year = criteria.searchTerm.dob.getFullYear()
				const month = criteria.searchTerm.dob.getMonth()
				const day = criteria.searchTerm.dob.getDate()
				result.items = result.items.filter(x => x.requestData.dob.getFullYear() === year && x.requestData.dob.getMonth() === month && x.requestData.dob.getDate() === day)
			}

			if (criteria.searchTerm.npis) {
				if (criteria.searchTerm.npis.length !== 0) {
					const names = criteria.searchTerm.npis.map(function(item) {
						return item.name.toLowerCase()
					})
					result.items = result.items.filter(x => names.indexOf(x.requestData.npi.toLowerCase()) !== -1)
				}
			}

			if (criteria.searchTerm.payers) {
				if (criteria.searchTerm.payers.length !== 0) {
					const names = criteria.searchTerm.payers.map(function(item) {
						return item.name
					})
					names.forEach(y => {
						result.items = result.items.filter(x => x.requestData.payerNames.indexOf(y) !== -1)
					})
				}
			}

			if (criteria.searchTerm.transactionStatus) {
				if (criteria.searchTerm.transactionStatus.length !== 0) {
					const ids = criteria.searchTerm.transactionStatus.map(function(item) {
						return item.id
					})
					result.items = result.items.filter(x => ids.indexOf(x.transactionStatusId) !== -1)
				}
			}

			if (criteria.searchTerm.runDate) {
				const year = criteria.searchTerm.runDate.getFullYear()
				const month = criteria.searchTerm.runDate.getMonth()
				const day = criteria.searchTerm.runDate.getDate()
				if (criteria.dateRange === 0) {
					result.items = result.items.filter(x => x.requestDate.getDate() === day && x.requestDate.getMonth() === month && x.requestDate.getFullYear() === year)
				}

				if (criteria.dateRange === 1) {
					result.items = result.items.filter(x => x.requestDate.getDate() <= day + 1 && x.requestDate.getDate() >= day - 1)
				}
				if (criteria.dateRange === 2) {
					result.items = result.items.filter(x => x.requestDate.getDate() <= day + 7 && x.requestDate.getDate() >= day - 7)
				}
				if (criteria.dateRange === 3) {
					result.items = result.items.filter(x => x.requestDate.getDate() <= day + 14 && x.requestDate.getDate() >= day - 14)
				}
				if (criteria.dateRange === 4) {
					result.items = result.items.filter(x => x.requestDate.getMonth() <= month + 1 && x.requestDate.getMonth() >= month - 1)
				}
				if (criteria.dateRange === 5) {
					result.items = result.items.filter(x => x.requestDate.getMonth() <= month + 6 && x.requestDate.getMonth() >= month - 6)
				}
				if (criteria.dateRange === 6) {
					result.items = result.items.filter(x => x.requestDate.getFullYear() <= year + 1 && x.requestDate.getFullYear() >= year - 1)
				}
			}
		}
		// sort
		const sortFunction = (x: ICoverageDiscoveryRequestHistoryGridRow, y: ICoverageDiscoveryRequestHistoryGridRow): number => {
			const getter = (i: ICoverageDiscoveryRequestHistoryGridRow): any => {
				if (criteria.sortField === 'id') {
					return i.id
				} else if (criteria.sortField === 'transactionStatusId') {
					return i.transactionStatusId
				} else if (criteria.sortField === 'reviewed') {
					return i.reviewed
				} else if (criteria.sortField === 'requestData') {
					return i.requestData.lastName
				} else if (criteria.sortField === 'requestor') {
					return i.requestor
				} else if (criteria.sortField === 'requestDate') {
					return i.requestData
				} else {
					throw new Error(`Unsupported sort column: [${criteria.sortField}]`)
				}
			}
			const xVal = getter(x)
			const yVal = getter(y)
			if (xVal === yVal) {
				return 0
			} else if (xVal < yVal) {
				return criteria.sortOrder === 'asc' ? -1 : 1
			} else {
				return criteria.sortOrder === 'asc' ? 1 : -1
			}
		}

		result.items.sort(sortFunction)
		result.total = result.items.length
		// paging
		result.items = result.items.slice((criteria.page - 1) * criteria.sizePerPage, (criteria.page - 1) * criteria.sizePerPage + criteria.sizePerPage)
		return new Promise<ICoverageDiscoveryRequestHistoryGridResults>(resolve => {
			setTimeout(() => resolve(result), 100)
		})
	}

	generateRandomNumberString(length: number): string {
		let result = ''
		const characters = '0123456789'
		const charactersLength = characters.length
		for (let i = 0; i < length; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength))
		}
		return result
	}

	updateReviewed(): Promise<any> {
		let result
		return new Promise<any>(resolve => {
			setTimeout(() => resolve(result), 100)
		})
	}
}
